import React, { useState, useCallback, useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { Container, Navbar, Nav, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { adminApi } from '../services/api';
import { DISPLAY_TIMEZONE, displayTimezoneLabel, formatTime } from '../utils/datetime';

interface AdminLayoutProps {
  onLogout: () => void;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ onLogout }) => {
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [refreshKey, setRefreshKey] = useState(0);
  const [showResubscribe, setShowResubscribe] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [resubscribeLoading, setResubscribeLoading] = useState(false);
  const [resubscribeResult, setResubscribeResult] = useState<{ status: string; previous_url: string; current_url: string; message: string } | null>(null);
  const [resubscribeError, setResubscribeError] = useState<string | null>(null);

  // S105 : l'interrupteur S102, lu à chaque rafraîchissement. `null` = inconnu.
  const [actionsActives, setActionsActives] = useState<boolean | null>(null);
  useEffect(() => {
    adminApi.getHealth()
      .then((h: { actions_enabled?: boolean }) =>
        setActionsActives(typeof h.actions_enabled === 'boolean' ? h.actions_enabled : null))
      .catch(() => setActionsActives(null));
  }, [refreshKey]);

  const handleRefresh = useCallback(() => {
    setLastUpdate(new Date());
    setRefreshKey(k => k + 1);
  }, []);

  const handleResubscribe = async () => {
    setResubscribeLoading(true);
    setResubscribeResult(null);
    setResubscribeError(null);
    try {
      const result = await adminApi.resubscribe(webhookUrl || undefined);
      setResubscribeResult(result);
    } catch (err: any) {
      setResubscribeError(err.response?.data?.detail || err.message || 'Réabonnement impossible');
    } finally {
      setResubscribeLoading(false);
    }
  };

  const handleCloseResubscribe = () => {
    setShowResubscribe(false);
    setWebhookUrl('');
    setResubscribeResult(null);
    setResubscribeError(null);
  };

  return (
    <div className="min-vh-100 bg-light">
      {/* Top Navbar */}
      <Navbar bg="dark" variant="dark" className="shadow-sm">
        <Container fluid>
          <Navbar.Brand className="fw-bold">
            <i className="bi bi-envelope-fill me-2"></i>
            Tri automatique des mails
          </Navbar.Brand>
          <Nav className="ms-auto">
            {actionsActives === true && (
              <span className="badge bg-success align-self-center me-3 px-3 py-2" title="L'agent exécute les actions : transferts, tags, rangements, dépôts SharePoint.">
                <i className="bi bi-play-circle-fill me-1"></i>
                Agent actif
              </span>
            )}
            {actionsActives === false && (
              <span className="badge bg-warning text-dark align-self-center me-3 px-3 py-2" title="Lecture seule : l'agent lit et classe, n'exécute aucune action.">
                <i className="bi bi-pause-circle-fill me-1"></i>
                Lecture seule
              </span>
            )}
            {actionsActives === null && (
              <span className="badge bg-secondary align-self-center me-3 px-3 py-2" title="Impossible de lire l'état de l'agent.">
                <i className="bi bi-question-circle me-1"></i>
                Mode inconnu
              </span>
            )}
            <small className="text-light me-3 align-self-center">
              <i className="bi bi-clock me-1"></i>
              Mis à jour : {formatTime(lastUpdate)}
              <span
                className="opacity-75 ms-2"
                title={`Toutes les heures sont affichées en ${DISPLAY_TIMEZONE}`}
              >
                <i className="bi bi-globe2 me-1"></i>
                {DISPLAY_TIMEZONE} ({displayTimezoneLabel()})
              </span>
            </small>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => setShowResubscribe(true)}
              className="me-2"
            >
              <i className="bi bi-arrow-repeat me-1"></i>
              Réabonner
            </Button>
            <Button
              variant="outline-light"
              size="sm"
              onClick={handleRefresh}
              className="me-2"
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Rafraîchir
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={onLogout}
            >
              <i className="bi bi-box-arrow-right me-1"></i>
              Déconnexion
            </Button>
          </Nav>
        </Container>
      </Navbar>

      {/* S105 : en lecture seule, un bandeau qu'on ne peut pas manquer. Clair et sur une
          ligne : le badge jaune de l'en-tête reste le seul jaune plein. */}
      {actionsActives === false && (
        <div role="status" style={{ background: '#fff8e1', borderBottom: '1px solid #f3dc8f', color: '#5c4500' }}>
          <Container fluid className="px-4 py-1 small">
            <i className="bi bi-pause-circle-fill me-2"></i>
            <strong>Lecture seule :</strong> les mails sont lus et classés, aucune action n'est
            exécutée. Les boîtes ne sont pas touchées.
          </Container>
        </div>
      )}

      {/* Navigation Pills */}
      <div className="bg-white border-bottom shadow-sm">
        <Container fluid className="px-4">
          <Nav className="onglets">
            <Nav.Item>
              <Nav.Link
                as={NavLink}
                to="/"
                end
                className="me-2"
              >
                <i className="bi bi-speedometer2 me-1"></i>
                Tableau de bord
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                as={NavLink}
                to="/emails"
                className="me-2"
              >
                <i className="bi bi-envelope-open me-1"></i>
                Mails
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                as={NavLink}
                to="/logs"
              >
                <i className="bi bi-journal-text me-1"></i>
                Journal des actions
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Container>
      </div>

      {/* Page Content */}
      <Container fluid className="px-4 py-4">
        <Outlet context={{ refreshKey, onRefresh: handleRefresh, onLogout, setLastUpdate }} />
      </Container>

      {/* Resubscribe Modal */}
      <Modal show={showResubscribe} onHide={handleCloseResubscribe} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-arrow-repeat me-2"></i>
            Renouveler les abonnements webhook
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Recrée les abonnements webhook Microsoft Graph. À utiliser après un changement
            d'URL du webhook dans Infisical, ou pour passer de la production à un tunnel local.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Webhook URL <span className="text-muted">(facultatif)</span></Form.Label>
            <Form.Control
              type="url"
              placeholder="Laisser vide pour garder la valeur actuelle"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              disabled={resubscribeLoading}
            />
            <Form.Text className="text-muted">
              Si vide, le serveur relit WEBHOOK_URL dans son environnement.
            </Form.Text>
          </Form.Group>

          {resubscribeError && (
            <Alert variant="danger" className="mb-0">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {resubscribeError}
            </Alert>
          )}

          {resubscribeResult && (
            <Alert variant="success" className="mb-0">
              <i className="bi bi-check-circle me-2"></i>
              <strong>{resubscribeResult.message}</strong>
              <div className="mt-2 small">
                <div><strong>Précédente :</strong> {resubscribeResult.previous_url}</div>
                <div><strong>Actuelle :</strong> {resubscribeResult.current_url}</div>
              </div>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseResubscribe}>
            Fermer
          </Button>
          <Button
            variant="warning"
            onClick={handleResubscribe}
            disabled={resubscribeLoading}
          >
            {resubscribeLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Réabonnement...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-repeat me-1"></i>
                Réabonner
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
